import React, { useEffect, useState } from "react";
import "./OTPVerification.css";
import { ToastContainer, toast } from "react-toastify";
import { Msg91Service } from "../../services/msg91.service";
import { CustomerApi } from "../../api/customer.api";
import BrowserDetails from "../utils/BrowserDetails";
import { LocalStorageUtils } from "../../utils/localstorage.utils.jsx";
import AnalyticsService from "../../services/analytics.service";
import { EVENT_NAME } from "utils/constants";
import { useNavigate } from "react-router-dom";
import { FcSms } from "react-icons/fc";
import { nanoid } from "nanoid";

const OTPVerification = ({ onSuccess, onClose }) => {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [step, setStep] = useState(1); // 1: Phone input, 2: OTP input
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [messageId, setMessageId] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState(
    Msg91Service.OTP_CHANNEL.SMS
  );
  const [resendChannel, setResendChannel] = useState(
    Msg91Service.OTP_CHANNEL.SMS
  );
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [operationId, setOperationId] = useState(nanoid());
  const navigate = useNavigate();

  useEffect(() => {
    AnalyticsService.trackEvent(EVENT_NAME.LOGIN_PAGE_VIEWED, {
      operationId,
    });
    Msg91Service.init();
  }, []);

  // Timer effect for resend functionality
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  const normalizePhoneNumber = (phone) => {
    if (!phone) return "";
    return phone.replace(/\D/g, "");
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    const normalizedPhone = normalizePhoneNumber(phone);

    if (!normalizedPhone || normalizedPhone.length < 10) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }

    if (!acceptedTerms) {
      setError("Please accept the terms and conditions");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const sendOtpResponse = await Msg91Service.sendOtp({
        phone: normalizedPhone,
        channel: Msg91Service.OTP_CHANNEL.SMS, // Always send via SMS initially
      });
      setMessageId(sendOtpResponse.messageId);
      AnalyticsService.trackEvent(EVENT_NAME.LOGIN_OTP_SENT, {
        operationId,
        phone: normalizedPhone,
        channel: Msg91Service.OTP_CHANNEL_NAME[selectedChannel],
        messageId: sendOtpResponse.messageId,
      });
      setStep(2);
      setResendTimer(60);
      setCanResend(false);
      toast.success("OTP sent successfully!", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to connect to server");
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    const normalizedPhone = normalizePhoneNumber(phone);
    const fullOtp = otp.join("");

    if (fullOtp.length !== 4) {
      setError("Please enter a valid 4-digit OTP");
      return;
    }

    if (!normalizedPhone) {
      setError("Phone number is required for verification");
      return;
    }

    setLoading(true);
    setError("");

    try {
      AnalyticsService.trackEvent(EVENT_NAME.LOGIN_OTP_VERIFICATION_ATTEMPTED, {
        operationId,
        phone: normalizedPhone,
        channel: Msg91Service.OTP_CHANNEL_NAME[selectedChannel],
        messageId,
      });
      const verifyOtpResponse = await Msg91Service.verifyOtp({
        otp: fullOtp,
        messageId,
      });
      AnalyticsService.trackEvent(EVENT_NAME.LOGIN_OTP_VERIFICATION_SUCCESS, {
        operationId,
        phone: normalizedPhone,
        channel: Msg91Service.OTP_CHANNEL_NAME[selectedChannel],
        messageId,
      });
      const otpToken = verifyOtpResponse.otpToken;
      await CustomerApi.login({
        mobile: Msg91Service.phoneNumber(normalizedPhone),
        otpToken,
      });
      console.log("mobileotp.jsx", "login success");
      AnalyticsService.trackEvent(EVENT_NAME.LOGIN_SUCCESS, {
        operationId,
        phone: normalizedPhone,
        channel: Msg91Service.OTP_CHANNEL_NAME[selectedChannel],
        messageId,
      });
      onSuccess();
    } catch (err) {
      console.log("handleVerifyOTP error", err);
      AnalyticsService.trackEvent(EVENT_NAME.LOGIN_FAILED, {
        operationId,
        phone: normalizedPhone,
        channel: Msg91Service.OTP_CHANNEL_NAME[selectedChannel],
        messageId,
        error: err.response?.data?.message || err.message,
      });
      setError(err.response?.data?.message || "OTP verification failed");
      setLoading(false);
    }
  };

  const handleResendOTP = async (channel = resendChannel) => {
    const normalizedPhone = normalizePhoneNumber(phone);

    if (!normalizedPhone) {
      setError("Phone number is required");
      return;
    }

    if (!canResend) {
      return;
    }

    setLoading(true);
    setError("");

    setSelectedChannel(channel);
    AnalyticsService.trackEvent(EVENT_NAME.RESEND_OTP, {
      operationId,
      phone: normalizedPhone,
      channel: Msg91Service.OTP_CHANNEL_NAME[channel],
      messageId: messageId,
    });

    try {
      const resendOtpResponse = await Msg91Service.retryOtp({
        messageId,
        channel,
      });
      setOtp(["", "", "", ""]);
      setResendTimer(60);
      setCanResend(false);
      toast.success("OTP resent successfully!", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } catch (err) {
      console.log("handleResendOTP error", err);
      setError(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (element, index) => {
    if (isNaN(element.value)) return false;

    const newOtp = [...otp];
    newOtp[index] = element.value;
    setOtp(newOtp);

    // Focus next input
    if (element.value && element.nextSibling) {
      element.nextSibling.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !e.target.value && e.target.previousSibling) {
      e.target.previousSibling.focus();
    }
  };

  const openTermsPage = () => {
    window.open("/terms-and-conditions", "_blank", "noopener,noreferrer");
  };

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <>
      <BrowserDetails
        setDeviceDetails={(browserDetails) => {
          LocalStorageUtils.set(
            LocalStorageUtils.LOCAL_STORAGE_KEY.WEB_DEVICE_DETAILS,
            browserDetails
          );
        }}
      />
      <div className="otp-modal-overlay">
        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />

        <div className="otp-modal">
          <div className="modal-header">
            <img src="/bm_icon.png" alt="Company Logo" className="modal-logo" />
            <div className="modal-title-header">Sign in with OTP</div>
            <div></div>
          </div>

          <div className="modal-content">
            <h2 className="modal-title">
              {step === 1 ? (
                <span
                  style={{ display: "flex", alignItems: "center", gap: "5px" }}
                >
                  Enter Mobile Number
                  <img
                    src="/whatsapp_icon.png"
                    alt="Company Logo"
                    className="modal-logo"
                  />
                </span>
              ) : (
                "Verify OTP"
              )}
            </h2>

            {error && <div className="message error">{error}</div>}

            {step === 1 ? (
              <form className="otp-form" onSubmit={handleSendOTP}>
                <div className="form-group">
                  <input
                    type="tel"
                    required
                    placeholder="Enter 10-digit mobile number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="phone-input"
                    maxLength="10"
                  />
                </div>

                <div className="terms-checkbox">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                  />
                  <label htmlFor="acceptTerms">
                    I agree to the{" "}
                    <span className="terms-link" onClick={openTermsPage}>
                      Terms and Conditions
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  className="submit-button"
                  disabled={loading || !acceptedTerms}
                  title={
                    !acceptedTerms
                      ? "Please accept the terms and conditions"
                      : ""
                  }
                >
                  {loading
                    ? "Sending..."
                    : "Send OTP via SMS"}
                </button>
              </form>
            ) : (
              <>
                <div className="phone-display">
                  <div className="phone-info">
                    OTP sent to +91 {phone} via SMS
                  </div>
                  <button
                    type="button"
                    className="edit-number-button"
                    onClick={() => {
                      setStep(1);
                      setSelectedChannel(Msg91Service.OTP_CHANNEL.SMS);
                      setError("");
                      setOtp(["", "", "", ""]);
                      setResendTimer(0);
                      setCanResend(false);
                      AnalyticsService.trackEvent(EVENT_NAME.EDIT_NUMBER, {
                        operationId,
                      });
                    }}
                  >
                    Edit Number
                  </button>
                </div>

                <form className="otp-form" onSubmit={handleVerifyOTP}>
                  <div className="otp-input-group">
                    {[0, 1, 2, 3].map((index) => (
                      <input
                        key={index}
                        type="text"
                        inputMode="numeric"
                        maxLength="1"
                        value={otp[index]}
                        onChange={(e) => handleOtpChange(e.target, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        className="otp-digit"
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>

                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? "Verifying..." : "Verify & Continue"}
                  </button>
                </form>

                <div className="resend-section">
                  <div className="resend-timer">
                    {resendTimer > 0 ? (
                      <span>Resend OTP in {formatTimer(resendTimer)}</span>
                    ) : (
                      <span>Didn't receive OTP?</span>
                    )}
                  </div>

                  {canResend && (
                    <>
                      <div className="resend-buttons">
                        <button
                          type="button"
                          className="resend-button"
                          disabled={loading}
                          onClick={() => handleResendOTP(Msg91Service.OTP_CHANNEL.SMS)}
                        >
                          Resend OTP via SMS <FcSms />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default OTPVerification;
